'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useTrackNavigation } from '@/hooks/useNavigationStore';
import { IEvent } from '@/lib/types';
import { SupportedLanguages } from '@/types/globals';
import { formatDate, formatTime } from '@/lib/dateUtils';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  LocationFilters,
  SearchBar,
  EventCard,
  RegularEventCard,
} from '@/components/events';
import { CalendarViewToggle } from '@/components/events/CalendarViewToggle';

// Custom hook for client-side hydration safety
const useClientSide = () => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  return isClient;
};

// Custom hook for event search using TanStack Query
const useEventSearch = (searchQuery: string, lang: string) => {
  return useQuery({
    queryKey: ['events', 'search', searchQuery, lang],
    queryFn: async (): Promise<{ featured: IEvent[]; regular: IEvent[] }> => {
      if (!searchQuery.trim()) {
        return { featured: [], regular: [] };
      }

      // Direct WordPress API call like TestimonialsIndexPage
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_WP_JSON}/wp/v2/event?search=${encodeURIComponent(searchQuery)}&lang=${lang}&per_page=50`,
      );

      if (!response.ok) {
        throw new Error('Failed to fetch search results');
      }

      const events: IEvent[] = await response.json();

      // Filter by locale and display like the server-side logic
      const filteredEvents = events.filter(
        event => event.acf?.locale === lang && event.acf?.display === true,
      );

      const featured = filteredEvents.filter(
        (event: IEvent) => event.acf?.featured,
      );
      const regular = filteredEvents.filter(
        (event: IEvent) => !event.acf?.featured,
      );

      return { featured, regular };
    },
    enabled: !!searchQuery.trim(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });
};

const text = {
  fr: {
    searchPlaceholder: 'Rechercher un événement',
    seeDetails: 'Voir les détails complets',
    noEventsFound: 'Aucun événement trouvé',
    noEventsAvailable: "Aucun événement disponible à l'instant",
    previous: 'Précédent',
    next: 'Suivant',
  },
  en: {
    searchPlaceholder: 'Search For An Event',
    seeDetails: 'See Full Details',
    noEventsFound: 'No events found',
    noEventsAvailable: 'No events available at the moment',
    previous: 'Previous',
    next: 'Next',
  },
};

interface EventsClientProps {
  locationTerms: Array<{
    term_id: number;
    name: string;
    slug: string;
    term_group: number;
    term_taxonomy_id: number;
    taxonomy: string;
    description: string;
    parent: number;
    count: number;
    filter: string;
  }>;
  featuredEvents: IEvent[];
  regularEvents: IEvent[];
  basePath: string;
  currentPage: number;
  totalPages: number;
  totalEvents: number;
  lang: SupportedLanguages;
}

export function EventsClient({
  locationTerms,
  featuredEvents,
  regularEvents,
  basePath,
  currentPage,
  totalPages,
  lang,
}: EventsClientProps) {
  const [selectedLocation, setSelectedLocation] = useState<number | null>(null);
  const isClient = useClientSide();
  const [searchQuery, setSearchQuery] = useState('');
  useTrackNavigation();

  // Filter location terms to only include those with upcoming events
  const getLocationTermsWithUpcomingEvents = () => {
    if (!isClient) return locationTerms;

    const today = dayjs().startOf('day');

    const allUpcomingEvents = [...featuredEvents, ...regularEvents].filter(
      event => {
        // Compare dates only (not times) to include events happening today
        // Use dayjs for consistent date parsing
        if (event.acf?.event_date_start) {
          const eventStartDate = dayjs(event.acf.event_date_start).startOf(
            'day',
          );
          if (eventStartDate.isSame(today) || eventStartDate.isAfter(today)) {
            return true;
          }
        }

        if (event.acf?.event_date_end) {
          const eventEndDate = dayjs(event.acf.event_date_end).startOf('day');
          if (eventEndDate.isSame(today) || eventEndDate.isAfter(today)) {
            return true;
          }
        }

        return false;
      },
    );

    const locationIdsWithEvents = new Set<number>();

    allUpcomingEvents.forEach(event => {
      // Check ACF location field
      if (event.acf?.event_location?.term_id) {
        locationIdsWithEvents.add(event.acf.event_location.term_id);
      }

      // Check taxonomy location field
      if (event.event_location) {
        event.event_location.forEach(locationId => {
          locationIdsWithEvents.add(locationId);
        });
      }
    });

    return locationTerms.filter(term =>
      locationIdsWithEvents.has(term.term_id),
    );
  };

  const filteredLocationTerms = getLocationTermsWithUpcomingEvents();

  // Reset selected location if it's no longer available in filtered terms
  useEffect(() => {
    if (selectedLocation !== null && isClient) {
      const isLocationStillAvailable = filteredLocationTerms.some(
        term => term.term_id === selectedLocation,
      );
      if (!isLocationStillAvailable) {
        setSelectedLocation(null);
      }
    }
  }, [filteredLocationTerms, selectedLocation, isClient]);

  // Use TanStack Query for search
  const searchQueryResult = useEventSearch(searchQuery, lang);
  const searchResults = searchQueryResult.data;
  const isSearching = searchQueryResult.isLoading;

  // Format functions using date-fns
  const formatDateLocal = (dateString: string) => {
    if (!isClient) return { year: '', day: '', month: '' };
    const formatted = formatDate(dateString, lang);
    return {
      year: formatted.year.toString(),
      day: formatted.day.toString(),
      month: formatted.month,
    };
  };

  const formatTimeLocal = (timeString: string) => {
    if (!timeString || !isClient) return '';
    return formatTime(timeString, lang);
  };

  // Helper function to filter events by location
  const filterEventsByLocation = (events: IEvent[]) => {
    // Don't filter during SSR
    if (!isClient) {
      return events;
    }

    // If no location is selected, show all events
    if (selectedLocation === null) {
      return events;
    }

    return events.filter(event => {
      const acfLocation = event.acf?.event_location;
      const taxonomyLocations = event.event_location;

      // Check ACF location field (single object)
      if (acfLocation && acfLocation.term_id === selectedLocation) {
        return true;
      }

      // Check taxonomy location field (array of term IDs)
      if (taxonomyLocations && taxonomyLocations.includes(selectedLocation)) {
        return true;
      }

      return false;
    });
  };

  // Get filtered events - only filter on client side
  const getFilteredEvents = () => {
    // If searching and we have results, use search results
    if (searchQuery.trim() && searchResults) {
      return {
        featured: filterEventsByLocation(searchResults.featured),
        regular: filterEventsByLocation(searchResults.regular),
      };
    }

    // Normal filtering (not searching)
    return {
      featured: filterEventsByLocation(featuredEvents),
      regular: filterEventsByLocation(regularEvents),
    };
  };

  const { featured: filteredFeaturedEvents, regular: filteredRegularEvents } =
    getFilteredEvents();

  return (
    <div className="min-h-screen bg-white">
      {/* Header Section */}
      <div className="mt-12 bg-white py-6">
        <div className="mx-auto max-w-7xl px-4">
          {/* View Toggle - Hidden on mobile and small tablets */}
          <div className="hidden justify-end lg:flex">
            <CalendarViewToggle locale={lang} basePath={basePath} />
          </div>
          {/* Filters and Search */}
          <div className="flex flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-center">
            <LocationFilters
              locationTerms={filteredLocationTerms}
              selectedLocation={selectedLocation}
              onLocationChange={setSelectedLocation}
              locale={lang}
            />

            <SearchBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              placeholder={text[lang || 'en'].searchPlaceholder}
              isSearching={isSearching}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-7xl px-4 py-8">
        {/* Featured Events */}
        {filteredFeaturedEvents.length > 0 && currentPage === 1 && (
          <div className="space-y-8">
            {filteredFeaturedEvents
              .filter(event => {
                // Compare dates only (not times) to include events happening today
                // Use dayjs for consistent date parsing
                const today = dayjs().startOf('day');

                if (event.acf?.event_date_start) {
                  const eventStartDate = dayjs(
                    event.acf.event_date_start,
                  ).startOf('day');
                  if (
                    eventStartDate.isSame(today) ||
                    eventStartDate.isAfter(today)
                  ) {
                    return true;
                  }
                }

                if (event.acf?.event_date_end) {
                  const eventEndDate = dayjs(event.acf.event_date_end).startOf(
                    'day',
                  );
                  if (
                    eventEndDate.isSame(today) ||
                    eventEndDate.isAfter(today)
                  ) {
                    return true;
                  }
                }

                return false;
              })
              .map((featuredEvent, index) => (
                <EventCard
                  key={featuredEvent.slug}
                  event={featuredEvent}
                  basePath={basePath}
                  lang={lang}
                  showDivider={index > 0}
                  formatDateLocal={formatDateLocal}
                  formatTimeLocal={formatTimeLocal}
                  acfText={text[lang || 'en']}
                  locale={lang}
                />
              ))}
          </div>
        )}

        {/* Regular Events */}
        <div className="space-y-6">
          {filteredRegularEvents
            .filter(event => {
              // Compare dates only (not times) to include events happening today
              // Use dayjs for consistent date parsing
              const today = dayjs().startOf('day');

              if (event.acf?.event_date_start) {
                const eventStartDate = dayjs(
                  event.acf.event_date_start,
                ).startOf('day');
                if (
                  eventStartDate.isSame(today) ||
                  eventStartDate.isAfter(today)
                ) {
                  return true;
                }
              }

              if (event.acf?.event_date_end) {
                const eventEndDate = dayjs(event.acf.event_date_end).startOf(
                  'day',
                );
                if (eventEndDate.isSame(today) || eventEndDate.isAfter(today)) {
                  return true;
                }
              }

              return false;
            })
            .map((event, index) => (
              <div key={event.slug} className="overflow-hidden rounded-lg">
                {index > 0 && (
                  <div className="mb-6 border-t border-gray-200"></div>
                )}
                <RegularEventCard
                  event={event}
                  basePath={basePath}
                  formatDateLocal={formatDateLocal}
                  formatTimeLocal={formatTimeLocal}
                  acfText={text[lang || 'en']}
                  locale={lang}
                />
              </div>
            ))}
          {/* Bottom divider after last regular event */}
          {filteredRegularEvents.length > 0 && (
            <div className="mt-6 border-t border-gray-200"></div>
          )}
        </div>

        {/* No Results */}
        {filteredFeaturedEvents.length === 0 &&
          filteredRegularEvents.length === 0 && (
            <div className="py-12 text-center">
              <p className="text-lg text-gray-500">
                {searchQuery.trim()
                  ? text[lang || 'en'].noEventsFound
                  : text[lang || 'en'].noEventsAvailable}
              </p>
            </div>
          )}

        {totalPages > 1 && !searchQuery.trim() && !selectedLocation && (
          <div className="mt-12 flex justify-center">
            <div className="flex items-center gap-2">
              {currentPage > 1 && (
                <Link
                  href={`/${basePath}?p=${currentPage - 1}`}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
                >
                  {text[lang || 'en'].previous}
                </Link>
              )}

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <Link
                      key={pageNum}
                      href={`/${basePath}?p=${pageNum}`}
                      className={`rounded-lg px-3 py-2 text-sm font-medium ${
                        pageNum === currentPage
                          ? 'bg-qf-purple text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </Link>
                  );
                })}
              </div>

              {currentPage < totalPages && (
                <Link
                  href={`/${basePath}?p=${currentPage + 1}`}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
                >
                  {text[lang || 'en'].next}
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
